import { CSSProperties, useContext, createContext } from "react";

const puzzleJsFolderPath = "/static/puzzlejs/";

// register some puzzle modes; a mode is just a set of options,
// so the options do not need to all be learned and manually applied to each puzzle.
// a puzzle can have multiple modes and multiple custom options.
const puzzleModes: { [key: string]: Partial<Options> } = {};

type Options = {
  "data-text": string | null;
  "data-text-replacements": { [key: string]: string } | null;
  "data-text-characters": string;
  "data-text-shift-key": "rebus" | "candidates" | "none";
  "data-text-shift-lock": boolean;
  "data-text-solution": string | null;
  "data-text-advance-on-type": boolean;

  // fills
  "data-fill-classes": string[] | null;
  "data-fills": string[] | null;
  "data-fill-cycle": boolean;

  // paths
  "data-paths": null;
  "data-path-max-directions": number;
  "data-path-style": string;

  // edges
  "data-edges": "3x3" | null;
  "data-edge-style": string;

  // spokes
  "data-spokes": string | null;
  "data-spoke-max-directions": number | null;
  "data-spoke-allowed-directions": "*" | "+" | "x" | "X";
  "data-spoke-levels": number;
  "data-spoke-style": string;

  // clues
  "data-clue-locations": "crossword" | "all" | null;
  "data-clue-indicators": string[] | null;
  "data-top-clues": string[] | null;
  "data-bottom-clues": string[] | null;
  "data-left-clues": string[] | null;
  "data-right-clues": string[] | null;

  // misc
  "data-drag-paint-fill": boolean;
  "data-drag-draw-path": boolean;
  "data-drag-draw-edge": boolean;
  "data-drag-draw-spoke": boolean;
  "data-unselectable-givens": boolean;
  "data-extracts": null;
  "data-no-input": boolean;
  "data-no-screenreader": boolean;
  "data-puzzle-id": null;
  "data-team-id": null;
  "data-player-id": null;
};

const defaultOptions: Options = {
  // text
  "data-text": null,
  "data-text-replacements": null,
  "data-text-characters": "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  "data-text-shift-key": "rebus",
  "data-text-shift-lock": false,
  "data-text-solution": null,
  "data-text-advance-on-type": false,

  // fills
  "data-fill-classes": null,
  "data-fills": null,
  "data-fill-cycle": true,

  // paths
  "data-paths": null,
  "data-path-max-directions": 2,
  "data-path-style": "straight",

  // edges
  "data-edges": null,
  "data-edge-style": "box",

  // spokes
  "data-spokes": null,
  "data-spoke-max-directions": null,
  "data-spoke-allowed-directions": "*",
  "data-spoke-levels": 1,
  "data-spoke-style": "solid",

  // clues
  "data-clue-locations": null,
  "data-clue-indicators": null,
  "data-top-clues": null,
  "data-bottom-clues": null,
  "data-left-clues": null,
  "data-right-clues": null,

  // misc
  "data-drag-paint-fill": true,
  "data-drag-draw-path": false,
  "data-drag-draw-edge": false,
  "data-drag-draw-spoke": false,
  "data-unselectable-givens": false,
  "data-extracts": null,
  "data-no-input": false,
  "data-no-screenreader": false,
  "data-puzzle-id": null,
  "data-team-id": null,
  "data-player-id": null,
};

puzzleModes["default"] = defaultOptions;

puzzleModes["linear"] = {
  "data-text-advance-on-type": true,
  "data-unselectable-givens": true,
};

puzzleModes["crossword"] = {
  "data-text-advance-on-type": true,
  "data-clue-locations": "crossword",
};

puzzleModes["notext"] = {
  "data-text-characters": "",
};

puzzleModes["sudoku"] = {
  "data-text-characters": "123456789",
  "data-edges": "3x3",
  "data-text": "9x9",
  "data-text-shift-key": "candidates",
};

puzzleModes["pathpaint"] = {
  "data-path-style": "curved",
  "data-drag-draw-path": true,
  "data-fill-cycle": false,
};

puzzleModes["trains"] = {
  "data-path-style": "track",
  "data-drag-paint-fill": false,
  "data-fill-cycle": false,
  "data-drag-draw-path": true,
};

puzzleModes["slitherlink"] = {
  "data-drag-draw-edge": true,
  "data-edge-style": "dots",
};

puzzleModes["spokes"] = {
  "data-drag-draw-spoke": true,
};

puzzleModes["wordsearch"] = {
  "data-drag-draw-spoke": true,
};

puzzleModes["slant"] = {
  "data-drag-draw-spoke": true,
  "data-spoke-allowed-directions": "x",
  "data-edge-style": "offset",
};

puzzleModes["bridges"] = {
  "data-drag-draw-spoke": true,
  "data-drag-paint-fill": false,
  "data-fill-cycle": false,
  "data-spoke-allowed-directions": "+",
  "data-spoke-levels": 2,
};

puzzleModes["solution"] = {
  "data-no-input": true,
};

const OptionsContext = createContext<Options>(defaultOptions);

function getOptionArray(
  input: string | string[] | null,
  splitchar: string
): string[] | null {
  if (input === null) return null;
  if (Array.isArray(input)) return input;
  return input.split(splitchar);
}

function getOptionDict<T extends keyof Options>(
  options: Options,
  option: T
): Options[T] {
  const val = options[option];
  // TODO attribute version
  return val;
}

type Clues = {
  clues: string[][];
  depth: number;
};

function EmptyOuterCell(props: { colIndex: number }) {
  const screenreaderSupported = useScreenreaderSupported();
  if (screenreaderSupported) {
    return (
      <td
        role="cell"
        aria-colindex={props.colIndex}
        aria-label="An empty cell in the outer clue area"
        className="cell outer-cell unselectable"
      />
    );
  }
  return <td className="cell outer-cell unselectable" />;
}

function OuterClue(props: {
  clues: string[];
  clueIndex: number;
  colIndex: number;
  zone: "top" | "left" | "right" | "bottom";
}) {
  const { clues, clueIndex, colIndex, zone } = props;
  const screenreaderSupported = useScreenreaderSupported();

  const role = screenreaderSupported ? "cell" : undefined;
  const ariaColIndex = screenreaderSupported ? colIndex : undefined;

  if (clueIndex < 0 || clueIndex >= clues.length || !clues[clueIndex]) {
    return (
      <td
        className="cell outer-cell unselectable"
        aria-label={
          screenreaderSupported
            ? "An empty clue in the " + zone + " area"
            : undefined
        }
        role={role}
        aria-colindex={ariaColIndex}
      />
    );
  }

  const className = `cell outer-cell zone-${props.zone}`;

  const ariaLabel = screenreaderSupported
    ? clues[clueIndex] + ": A clue in the " + zone + " clue area"
    : undefined;
  return (
    <td
      role={role}
      aria-colindex={ariaColIndex}
      aria-label={ariaLabel}
      className={className}
    >
      {clues[clueIndex]}
    </td>
  );
}

function SVGEdge(props: { edgeName: string }) {
  const { edgeName } = props;
  let { "data-edge-style": edgePath } = useContext(OptionsContext);
  if (!edgePath.endsWith(".svg")) {
    edgePath = puzzleJsFolderPath + "edge-" + edgePath + ".svg";
  }

  return <use className={edgeName} href={edgePath + "#" + edgeName} />;
}

function SVGSpokes(props: {
  spokeCode: number | undefined;
  spokePrefix: "" | "x-";
  spokeSuffix: string;
}) {
  const { spokeCode, spokePrefix, spokeSuffix } = props;

  let { "data-spoke-style": spokePath } = useContext(OptionsContext);

  if (!spokeCode) return null;

  if (!spokePath.endsWith(".svg")) {
    spokePath = puzzleJsFolderPath + "spoke-" + spokePath + ".svg";
  }

  return (
    <>
      {Array.from(Array(8).keys()).map((i) =>
        spokeCode & (1 << i) ? (
          <use
            key={i}
            transform={"rotate" + (i >> 1) * 90 + ")"}
            href={
              spokePath +
              "#" +
              spokePrefix +
              "spoke-n" +
              (i & 1 ? "e" : "") +
              spokeSuffix
            }
          />
        ) : null
      )}
    </>
  );
}

const pathTranslate = [
  "o0",
  "i2",
  "i0",
  "l0",
  "i1",
  "r2",
  "r1",
  "t1",
  "i3",
  "r3",
  "r0",
  "t3",
  "l1",
  "t2",
  "t0",
  "x0",
];

function SVGPath(props: { pathCode: number }) {
  const { pathCode } = props;
  const translatedData = pathTranslate[pathCode];
  let { "data-path-style": pathPath } = useContext(OptionsContext);

  if (!pathCode) return null;

  if (!pathPath.endsWith(".svg")) {
    pathPath = puzzleJsFolderPath + "path-" + pathPath + ".svg";
  }

  return (
    <use
      className="path"
      href={pathPath + "#path-" + translatedData[0]}
      transform={
        translatedData[1] === "0"
          ? undefined
          : "rotate(" + parseInt(translatedData[1]) * 90 + ")"
      }
    />
  );
}

function GridSVG(props: { cellInfo: CellInfo }) {
  const {
    cellInfo: {
      pathCode,
      edgeCode = 0,
      unselectable,
      xEdgeCode = 0,
      spokeCode = 0,
      xSpokeCode = 0,
      spokeCodes,
    },
  } = props;
  const { "data-spoke-levels": dataSpokeLevels } = useContext(OptionsContext);

  const spokeLevels: number[] = [];
  for (let l = 2; l <= dataSpokeLevels + 1; l++) {
    spokeLevels.push(l);
  }

  return (
    <svg aria-hidden="true" viewBox="-10 -10 20 20">
      {pathCode ? <SVGPath pathCode={pathCode} /> : null}
      {unselectable ? null : <SVGEdge edgeName="edge-base" />}
      {edgeCode & 1 ? <SVGEdge edgeName="edge-top" /> : null}
      {edgeCode & 2 ? <SVGEdge edgeName="edge-bottom" /> : null}
      {edgeCode & 4 ? <SVGEdge edgeName="edge-left" /> : null}
      {edgeCode & 8 ? <SVGEdge edgeName="edge-right" /> : null}
      {xEdgeCode & 1 ? <SVGEdge edgeName="x-edge-top" /> : null}
      {xEdgeCode & 2 ? <SVGEdge edgeName="x-edge-bottom" /> : null}
      {xEdgeCode & 4 ? <SVGEdge edgeName="x-edge-left" /> : null}
      {xEdgeCode & 8 ? <SVGEdge edgeName="x-edge-right" /> : null}
      <SVGSpokes spokeCode={spokeCode} spokePrefix="" spokeSuffix="" />
      <SVGSpokes spokeCode={xSpokeCode} spokePrefix="x-" spokeSuffix="" />
      {spokeCodes
        ? spokeLevels.map((l) => (
            <SVGSpokes
              key={l}
              spokeCode={spokeCodes.get(l)}
              spokePrefix=""
              spokeSuffix={"-" + l.toString()}
            />
          ))
        : null}
    </svg>
  );
}

function translate(
  ch: string,
  replacements: { [key: string]: string } | null
): string {
  if (!replacements?.[ch]) return ch;
  return replacements[ch];
}

function parseOuterClues(clueTemplate: string[] | null): Clues | null {
  if (clueTemplate === null) return null;
  let depth = 0;
  const clues: string[][] = [];
  for (let i = 0; i < clueTemplate.length; i++) {
    clues.push(clueTemplate[i].split(" "));
    depth = Math.max(clues[i].length, depth);
  }
  return { clues, depth };
}

function GridCell(props: {
  cellInfo: CellInfo;
  colIndex: number;
  isCopyjackVersion?: boolean;
}) {
  const { cellInfo, colIndex, isCopyjackVersion = false } = props;

  const {
    clue,
    extractCode,
    fill,
    row,
    column,
    blackCell,
    innerText,
    pathCode,
    edgeCode,
    classes,
  } = cellInfo;

  const screenreaderSupported = useScreenreaderSupported();
  const showBorders = fill !== "transparent";

  const style: CSSProperties = {};

  if (isCopyjackVersion && showBorders) {
    Object.assign(style, {
      borderTop: "1px solid black",
      borderBottom: "1px solid black",
      borderLeft: "1px solid black",
      borderRight: "1px solid black",
    });
  }

  if ((edgeCode ?? 0) & 1) style.borderTop = "3px solid black";
  if ((edgeCode ?? 0) & 2) style.borderBottom = "3px solid black";
  if ((edgeCode ?? 0) & 4) style.borderLeft = "3px solid black";
  if ((edgeCode ?? 0) & 8) style.borderRight = "3px solid black";

  return (
    <td
      role={screenreaderSupported ? "cell" : undefined}
      aria-colindex={screenreaderSupported ? colIndex : undefined}
      id={`cell-${row}-${column}`}
      className={`${classes.join(" ")}${fill ? ` ${fill} added-fill` : ""}${
        blackCell ? " black-cell unselectable" : ""
      }${pathCode ? " path-cell" : ""}`}
      style={style}
    >
      {clue ? (
        <div aria-hidden="true" className="clue">
          {clue.indicator}
        </div>
      ) : null}

      {extractCode ? (
        <div aria-hidden="true" className="extract-code">
          {extractCode}
        </div>
      ) : null}
      <div aria-hidden="true" className="text">
        <span>{innerText}</span>
      </div>
      {showBorders ? <GridSVG cellInfo={cellInfo} /> : null}
    </td>
  );
}

function useScreenreaderSupported() {
  const {
    "data-no-screenreader": noScreenreader,
    "data-top-clues": topClues,
    "data-left-clues": leftClues,
    "data-right-clues": rightClues,
    "data-bottom-clues": bottomClues,
  } = useContext(OptionsContext);
  if (noScreenreader || topClues || leftClues || rightClues || bottomClues) {
    return true;
  }
  return false;
}

type Clue = {
  indicator?: string;
  acrossClueNumber?: string;
  downClueNumber?: string;
  clue?: string;
  innerText?: string;
};

type CellInfo = {
  fill?: string;
  clue?: Clue;
  spokeCode?: number;
  spokeCodes?: Map<number, number>;
  xSpokeCode?: number;
  xSpokeCodes?: Map<number, number>;
  pathCode?: number;
  edgeCode?: number;
  xEdgeCode?: number;
  unselectable?: boolean;
  innerText?: string;
  blackCell?: boolean;
  extractCode?: string;
  autocapitalize?: string;
  contentEditable?: boolean;
  classes: string[];
  givenText?: boolean;
  column: number;
  row: number;
  ariaColIndex: number;
};

function PuzzleGridTable(props: {
  tableClassName?: string;
  data: {
    numCols: number;
    numRows: number;
    cellInfos: CellInfo[][];
    topClues: Clues | null;
    bottomClues: Clues | null;
    leftClues: Clues | null;
    rightClues: Clues | null;
  };
  isCopyjackVersion?: boolean;
}) {
  const {
    tableClassName,
    data: {
      numCols,
      numRows,
      cellInfos,
      topClues,
      bottomClues,
      leftClues,
      rightClues,
    },
    isCopyjackVersion = false,
  } = props;
  const options = useContext(OptionsContext);

  let screenreaderSupported = !options["data-no-screenreader"];
  if (
    topClues !== null ||
    bottomClues !== null ||
    leftClues !== null ||
    rightClues !== null
  ) {
    screenreaderSupported = false;
  }

  let label = `A ${numRows} row by ${numCols} column puzzle grid.`;
  let ariaRowCount = undefined;
  let ariaColCount = undefined;
  let role: "grid" | undefined = undefined;
  if (screenreaderSupported) {
    role = "grid";
    ariaRowCount = (topClues?.depth ?? 0) + (bottomClues?.depth ?? 0) + numRows;
    ariaColCount = (leftClues?.depth ?? 0) + (rightClues?.depth ?? 0) + numCols;
  } else {
    label +=
      " Unfortunately, this specific puzzle is not compatible with a screen reader.";
  }

  return (
    <table
      className={`puzzle-grid-content no-input ${tableClassName ?? ""} ${
        isCopyjackVersion ? "copy-only" : ""
      }`}
      role={role}
      aria-rowcount={ariaRowCount}
      aria-colcount={ariaColCount}
      aria-label={label}
    >
      <tbody>
        {topClues
          ? Array.from(Array(topClues.depth).keys()).map((i) => {
              return (
                <tr
                  key={i}
                  role={screenreaderSupported ? "row" : undefined}
                  aria-rowindex={screenreaderSupported ? i + 1 : undefined}
                >
                  {Array.from(Array(leftClues?.depth ?? 0).keys()).map((j) => (
                    <EmptyOuterCell key={j} colIndex={j} />
                  ))}
                  {topClues.clues.map((clue, j) => (
                    <OuterClue
                      key={j}
                      clues={clue}
                      clueIndex={i - topClues.depth + clue.length}
                      colIndex={1 + (leftClues?.depth ?? 0) + j}
                      zone="top"
                    />
                  ))}
                  {Array.from(Array(rightClues?.depth ?? 0).keys()).map((j) => (
                    <EmptyOuterCell
                      key={j}
                      colIndex={1 + (leftClues?.depth ?? 0) + numCols + j}
                    />
                  ))}
                </tr>
              );
            })
          : null}
        {cellInfos.map((infoList, r) => (
          <tr
            key={r}
            role={screenreaderSupported ? "row" : undefined}
            aria-rowindex={
              screenreaderSupported ? (topClues?.depth ?? 0) + r + 1 : undefined
            }
          >
            {leftClues
              ? Array.from(Array(leftClues.depth).keys()).map((i) => (
                  <OuterClue
                    key={i}
                    clues={leftClues.clues[r]}
                    zone="left"
                    clueIndex={i - leftClues.depth + leftClues.clues[r].length}
                    colIndex={1 + i}
                  />
                ))
              : null}
            {infoList.map((cellInfo, c) => (
              <GridCell
                cellInfo={cellInfo}
                key={c}
                colIndex={1 + (leftClues?.depth ?? 0) + c}
                isCopyjackVersion={isCopyjackVersion}
              />
            ))}
            {rightClues
              ? Array.from(Array(rightClues.depth).keys()).map((i) => (
                  <OuterClue
                    key={i}
                    clues={rightClues.clues[r]}
                    clueIndex={i}
                    colIndex={1 + (leftClues?.depth ?? 0) + numCols + i}
                    zone="right"
                  />
                ))
              : null}
          </tr>
        ))}
        {bottomClues
          ? Array.from(Array(bottomClues.depth).keys()).map((i) => (
              <tr
                key={i}
                role={screenreaderSupported ? "row" : undefined}
                aria-rowindex={
                  screenreaderSupported
                    ? (topClues?.depth ?? 0) + numRows + i + 1
                    : undefined
                }
              >
                {Array.from(Array(leftClues?.depth ?? 0).keys()).map((j) => (
                  <EmptyOuterCell key={j} colIndex={1 + j} />
                ))}
                {bottomClues.clues.map((clue, j) => (
                  <OuterClue
                    key={j}
                    clues={clue}
                    zone="bottom"
                    clueIndex={i}
                    colIndex={1 + (leftClues?.depth ?? 0) + j}
                  />
                ))}
                {Array.from(Array(rightClues?.depth ?? 0).keys()).map((j) => (
                  <EmptyOuterCell
                    key={j}
                    colIndex={1 + (leftClues?.depth ?? 0) + numCols + j}
                  />
                ))}
              </tr>
            ))
          : null}
      </tbody>
    </table>
  );
}

export type PuzzleGridProps = {
  id?: string;
  modes?: string[];
  text?: string;
  optionsOverride: Partial<Options>;
  tilt: number;
  isUsingCopyjack?: boolean;
  acrossClues?: string[];
  downClues?: string[];
  tableClassName?: string;
};

export function PuzzleGrid(props: PuzzleGridProps) {
  // Copyjack support.
  //
  // Reads from inputTd, a td that's part of an interactive puzzle element.
  // Expects that inputTd.dataset.copyjack is copyTd, a td that's part of this.copyjackVersion.
  // Styles copyTd such that clipboard copies it correctly.

  const {
    id,
    modes,
    text,
    optionsOverride,
    isUsingCopyjack = true,
    tableClassName,
  } = props;

  const options = { ...defaultOptions };
  for (const mode of modes ?? []) {
    Object.assign(options, puzzleModes[mode]);
  }
  Object.assign(options, optionsOverride);
  options["data-text"] = text ?? null;

  const clueIndicators = getOptionArray(options["data-clue-indicators"], " ");
  let textLines = getOptionArray(options["data-text"], "|");
  const textReplacements = getOptionDict(options, "data-text-replacements");
  const fillClasses = getOptionArray(options["data-fill-classes"], " ");
  const fills = getOptionArray(options["data-fills"], "|");
  const solution = getOptionArray(options["data-text-solution"], "|");

  // This gets overwritten sometimes.
  let edges = getOptionArray(options["data-edges"], "|");
  const paths = getOptionArray(options["data-paths"], "|");
  const maxSpokeLevels = options["data-spoke-levels"] + 1;
  const spokes = new Array(maxSpokeLevels);
  for (let l = 1; l < maxSpokeLevels; l++) {
    let spoke: string[] | undefined | null =
      l > 1
        ? getOptionArray(
            options[("data-spokes-" + l.toString()) as keyof Options] as
              | string
              | string[]
              | null,
            "|"
          )
        : getOptionArray(options["data-spokes"], "|");
    if (!spoke) {
      spoke = undefined;
    }
    spokes[l] = spoke;
  }
  const extracts = getOptionArray(options["data-extracts"], " ");

  const unselectableGivens = options["data-unselectable-givens"];

  const topClues = parseOuterClues(
    getOptionArray(options["data-top-clues"], "|")
  );
  const bottomClues = parseOuterClues(
    getOptionArray(options["data-bottom-clues"], "|")
  );
  const leftClues = parseOuterClues(
    getOptionArray(options["data-left-clues"], "|")
  );
  const rightClues = parseOuterClues(
    getOptionArray(options["data-right-clues"], "|")
  );

  const acrossClues: Clue[] = (props.acrossClues ?? []).map((clue) => {
    return { clue };
  });
  let acrossClueIndex = 0;
  const downClues: Clue[] = (props.downClues ?? []).map((clue) => {
    return { clue };
  });
  let downClueIndex = 0;

  if (!textLines) {
    textLines = solution;
  }
  if (textLines === null) throw new Error("textLines is null!");

  const allowInput = !options["data-no-input"];
  let clueNum = 0;
  let extractNum = 0;

  let regularRowBorder = 0;
  let regularColBorder = 0;

  if (textLines.length == 1 && /^\d+x\d+$/.test(textLines[0])) {
    const dim = textLines[0].split("x");
    textLines = [];
    for (let r = 0; r < parseInt(dim[1]); r++) {
      textLines[r] = ".".repeat(parseInt(dim[0]));
    }
  }

  if (edges && edges.length == 1 && /^\d+x\d+$/.test(edges[0])) {
    const dim = edges[0].split("x");
    edges = null;
    regularColBorder = parseInt(dim[0]);
    regularRowBorder = parseInt(dim[1]);
  }

  const numRows = textLines.length;
  let numCols = 0;

  const cellInfos: CellInfo[][] = [];

  for (let r = 0; r < textLines.length; r++) {
    cellInfos.push([]);

    numCols = Math.max(numCols, textLines[r].length);
    for (let c = 0; c < textLines[r].length; c++) {
      const cellInfo: CellInfo = {
        row: r,
        column: c,
        ariaColIndex: 1 + (leftClues?.depth ?? 0) + c,
        xSpokeCode: 0,
        classes: ["cell", "inner-cell"],
      };

      const ch = textLines[r][c];

      if (ch == ".") {
        if (solution) {
          cellInfo.innerText = translate(solution[r][c], textReplacements);
        } else if (allowInput && options["data-text-characters"]) {
          cellInfo.contentEditable = true;
          cellInfo.autocapitalize = "off";
        }
      } else if (ch == "#") {
        cellInfo.classes.push("extract");
        if (solution) {
          cellInfo.innerText = translate(solution[r][c], textReplacements);
        } else if (allowInput && options["data-text-characters"]) {
          cellInfo.contentEditable = true;
          cellInfo.autocapitalize = "off";
        }
        if (extracts) {
          const code = extracts[extractNum++];
          cellInfo.extractCode = code;
        }
      } else if (ch == "@") {
        cellInfo.blackCell = true;
        cellInfo.unselectable = true;
      } else {
        cellInfo.innerText = translate(ch, textReplacements);
        cellInfo.givenText = true;
        if (unselectableGivens) {
          cellInfo.unselectable = true;
        }
      }

      let edgeCode = 0;
      if (edges || regularRowBorder || regularColBorder) {
        if (regularRowBorder) {
          if (r % regularRowBorder == 0) {
            edgeCode |= 1;
          }
          if (r == textLines.length - 1) {
            edgeCode |= 2;
          }
        }
        if (regularColBorder) {
          if (c % regularColBorder == 0) {
            edgeCode |= 4;
          }
          if (c == textLines[r].length - 1) {
            edgeCode |= 8;
          }
        }

        if (edges) {
          if (edges.length == textLines.length) {
            edgeCode |= parseInt(edges[r][c], 16);
          } else if (edges.length == textLines.length * 2 + 1) {
            const topRow = edges[r * 2];
            const midRow = edges[r * 2 + 1];
            const botRow = edges[r * 2 + 2];
            const chTop =
              topRow.length == textLines[r].length
                ? topRow[c]
                : topRow[c * 2 + 1];
            const chLeft =
              midRow.length == textLines[r].length + 1
                ? midRow[c]
                : midRow[c * 2];
            const chRight =
              midRow.length == textLines[r].length + 1
                ? midRow[c + 1]
                : midRow[c * 2 + 2];
            const chBottom =
              botRow.length == textLines[r].length
                ? botRow[c]
                : botRow[c * 2 + 1];
            if (chTop != " " && chTop != ".") {
              edgeCode |= 1;
            }
            if (chBottom != " " && chBottom != ".") {
              edgeCode |= 2;
            }
            if (chLeft != " " && chLeft != ".") {
              edgeCode |= 4;
            }
            if (chRight != " " && chRight != ".") {
              edgeCode |= 8;
            }
          }
        }
      }
      cellInfo.edgeCode = edgeCode;

      let pathCode = 0;
      if (paths) {
        if (paths.length == textLines.length) {
          const obj: { [c: string]: number } = {
            "─": 12,
            "│": 3,
            "┌": 10,
            "┐": 6,
            "└": 9,
            "┘": 5,
          };
          pathCode |= obj[paths[r][c]] || parseInt(paths[r][c], 16);
        } else if (paths.length == textLines.length * 2 + 1) {
          const topRow = paths[r * 2];
          const midRow = paths[r * 2 + 1];
          const botRow = paths[r * 2 + 2];
          const chTop =
            topRow.length == textLines[r].length
              ? topRow[c]
              : topRow[c * 2 + 1];
          const chLeft =
            midRow.length == textLines[r].length + 1
              ? midRow[c]
              : midRow[c * 2];
          const chRight =
            midRow.length == textLines[r].length + 1
              ? midRow[c + 1]
              : midRow[c * 2 + 2];
          const chBottom =
            botRow.length == textLines[r].length
              ? botRow[c]
              : botRow[c * 2 + 1];
          if (chTop != " " && chTop != ".") {
            pathCode |= 1;
          }
          if (chBottom != " " && chBottom != ".") {
            pathCode |= 2;
          }
          if (chLeft != " " && chLeft != ".") {
            pathCode |= 4;
          }
          if (chRight != " " && chRight != ".") {
            pathCode |= 8;
          }
        }
      }
      cellInfo.pathCode = pathCode;

      for (let l = 1; l < maxSpokeLevels; l++) {
        let spokeCode = 0;
        if (spokes[l]) {
          const spoke = spokes[l];
          const topRow = spoke[r * 2];
          const midRow = spoke[r * 2 + 1];
          const botRow = spoke[r * 2 + 2];
          const chN = topRow[c * 2 + 1];
          const chNE = topRow[c * 2 + 2];
          const chE = midRow[c * 2 + 2];
          const chSE = botRow[c * 2 + 2];
          const chS = botRow[c * 2 + 1];
          const chSW = botRow[c * 2];
          const chW = midRow[c * 2];
          const chNW = topRow[c * 2];
          if (chN != " " && chN != ".") {
            spokeCode |= 1;
          }
          if (chNE == "/" || chNE == "'" || chNE == "X" || chNE == "x") {
            spokeCode |= 2;
          }
          if (chE != " " && chE != ".") {
            spokeCode |= 4;
          }
          if (chSE == "\\" || chSE == "`" || chSE == "X" || chSE == "x") {
            spokeCode |= 8;
          }
          if (chS != " " && chS != ".") {
            spokeCode |= 16;
          }
          if (chSW == "/" || chSW == "'" || chSW == "X" || chSW == "x") {
            spokeCode |= 32;
          }
          if (chW != " " && chW != ".") {
            spokeCode |= 64;
          }
          if (chNW == "\\" || chNW == "`" || chNW == "X" || chNW == "x") {
            spokeCode |= 128;
          }
        }
        if (spokeCode) {
          if (l > 1) {
            if (!cellInfo.spokeCodes) cellInfo.spokeCodes = new Map();
            cellInfo.spokeCodes.set(l, spokeCode);
          } else {
            cellInfo.spokeCode = spokeCode;
          }
        }
      }

      if (options["data-clue-locations"] && textLines[r][c] != "@") {
        const acrossClue =
          (c == 0 || textLines[r][c - 1] == "@" || edgeCode & 4) &&
          c < textLines[r].length - 1 &&
          textLines[r][c + 1] != "@" &&
          !(edgeCode & 8); // block/edge left, letter right
        const downClue =
          (r == 0 || textLines[r - 1][c] == "@" || edgeCode & 1) &&
          r < textLines.length - 1 &&
          textLines[r + 1][c] != "@" &&
          !(edgeCode & 2); // block/edge above, letter below

        if (acrossClue || downClue || options["data-clue-locations"] == "all") {
          const clueIndicator = !clueIndicators
            ? String(++clueNum)
            : clueIndicators[clueNum++];
          const isClueEmpty = clueIndicator.trim() === "";

          if (!isClueEmpty && options["data-clue-locations"] == "crossword") {
            if (acrossClue) {
              if (!cellInfo.clue) cellInfo.clue = {};
              cellInfo.clue.acrossClueNumber = clueIndicator;
            }
            if (downClue) {
              if (!cellInfo.clue) cellInfo.clue = {};
              cellInfo.clue.downClueNumber = clueIndicator;
            }
            if (acrossClue && acrossClues[acrossClueIndex]) {
              // TODO: track clues somehow?
              acrossClues[acrossClueIndex].acrossClueNumber = clueIndicator;
              acrossClueIndex++;
            }
            if (downClue && downClues[downClueIndex]) {
              downClues[downClueIndex].downClueNumber = clueIndicator;
              downClueIndex++;
            }
          }

          if (cellInfo.clue) {
            cellInfo.clue.indicator = clueIndicator;
          }
        }

        if (options["data-clue-locations"] == "crossword") {
          if (
            !acrossClue &&
            c > 0 &&
            textLines[r][c - 1] != "@" &&
            !(edgeCode & 4)
          ) {
            if (!cellInfo.clue) cellInfo.clue = {};
            cellInfo.clue.acrossClueNumber =
              cellInfos[r][c - 1].clue?.acrossClueNumber;
          }
          if (
            !downClue &&
            r > 0 &&
            textLines[r - 1][c] != "@" &&
            !(edgeCode & 1)
          ) {
            if (!cellInfo.clue) cellInfo.clue = {};
            cellInfo.clue.downClueNumber =
              cellInfos[r - 1][c].clue?.downClueNumber;
          }
        }
      }

      if (fillClasses) {
        let fillIndex = 0;
        if (fills && fills[r][c] != ".") {
          fillIndex = parseInt(fills[r][c], 36);
        } else {
          fillIndex = 0;
        }
        cellInfo.fill = fillClasses[fillIndex];
      }

      cellInfos[r].push(cellInfo);
    }
  }

  const tableData = {
    numCols,
    numRows,
    cellInfos,
    topClues,
    bottomClues,
    leftClues,
    rightClues,
  };

  return (
    <div id={id} className="puzzle-entry loaded">
      <OptionsContext.Provider value={options}>
        {isUsingCopyjack ? (
          <PuzzleGridTable
            tableClassName={tableClassName}
            data={tableData}
            isCopyjackVersion
          />
        ) : null}
        <PuzzleGridTable
          tableClassName={`${tableClassName} ${
            isUsingCopyjack ? "no-copy" : ""
          }`}
          data={tableData}
        />
      </OptionsContext.Provider>
      {acrossClues.length > 0 && downClues.length > 0 && (
        <>
          <h4>Across</h4>
          <ol>
            {acrossClues.map(({ clue, acrossClueNumber }) => (
              <li key={acrossClueNumber} value={acrossClueNumber}>
                {clue}
              </li>
            ))}
          </ol>
          <h4>Down</h4>
          <ol>
            {downClues.map(({ clue, downClueNumber }) => (
              <li key={downClueNumber} value={downClueNumber}>
                {clue}
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
}
